/**
 * The patient app shell, and the small design system every patient screen is
 * built from.
 *
 * Source: Brian, 23 September 2026: "I want the MCOS patient-facing interface
 * to match the attached designs EXACTLY from an aesthetic/UI standpoint ... I
 * want this same aesthetic to become the standard patient-facing MCOS design
 * system so every subsequent patient screen looks like it belongs to the exact
 * same product." The designs are mockups 4, 5 and 6 in
 * docs/design-refs-2026-09-23/.
 *
 * Every size here was measured off those images and divided by their scale
 * (the phone screen is about 690 image pixels wide for a 390px phone), and
 * every colour was sampled from them. Where the three mockups disagree with
 * each other (the bell is navy on one and green on another), the dashboard
 * wins, because it is the screen a patient sees most.
 *
 * The chrome: a serif MCOS wordmark with its tagline, a bell that lights when
 * something is waiting, the patient's initials, a menu, and a bottom tab bar
 * whose active tab is drawn filled. The column is phone width and centred, so
 * the same code is a sensible desktop page rather than a stretched phone.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { IdleSignOut } from '../../components/IdleSignOut';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { portalRequest, usePortal } from '../../lib/portal';
import { Spinner } from '../../components/ui';
import {
  ArrowLeft,
  ArrowRight,
  BarsLine,
  BarsSolid,
  Bell,
  ChevronDown,
  Close,
  DocLine,
  DocSolid,
  HeartLine,
  HeartSolid,
  HomeLine,
  HomeSolid,
  Menu,
  UserLine,
  UserSolid,
} from './icons';

/* ================================================================== *
 * Tokens
 * ================================================================== *
 *
 * The shared palette in index.css carries the brand colours. These are the
 * few extra values the phone screens need, each sampled from the mockups.
 */

export const TONE = {
  /** Headings and big numbers: the mockups' near-black navy. */
  heading: '#101c30',
  /** Body copy, subtitles, labels. */
  body: '#363f4e',
  /** Axis labels, captions, the quietest text. */
  faint: '#5b6472',
  /** Card and tile borders: lighter than the clinic's hairline, as drawn. */
  line: '#e6e4df',
  /** The inset rows on the plan card. */
  row: '#f2f3f1',
  /** Link and active-label green, darker than the button fill. */
  forestText: '#0a3d30',
  /** Initials circle. */
  avatar: '#ebe8e4',
} as const;

/**
 * The display serif, set at a large optical size.
 *
 * The mockups' headings are a high-contrast, fairly narrow serif. Fraunces at
 * its automatic optical size is rounder and wider at 26px than what is drawn;
 * pinned at a display optical size it matches both the contrast and the
 * measured widths. The font is loaded with the full optical size axis.
 */
export const DISPLAY: CSSProperties = { fontVariationSettings: '"opsz" 84' };
export const DISPLAY_MID: CSSProperties = { fontVariationSettings: '"opsz" 48' };

/* ================================================================== *
 * Data
 * ================================================================== */

/** One fetch helper, so every portal screen loads and fails the same way. */
export function usePortalQuery<T>(key: string, path: string, enabled = true) {
  return useQuery({
    queryKey: ['portal', key, path],
    queryFn: () => portalRequest<T>(path),
    enabled,
    retry: false,
  });
}

export interface PortalNotice {
  kind: 'checkin' | 'report' | 'plan';
  title: string;
  body: string;
  at: string;
  href: string | null;
  externalHref: string | null;
}

/* ================================================================== *
 * Shell
 * ================================================================== */

const TABS = [
  { to: '/portal', label: 'Dashboard', line: HomeLine, solid: HomeSolid, end: true },
  { to: '/portal/health', label: 'My Health', line: HeartLine, solid: HeartSolid, end: false },
  { to: '/portal/progress', label: 'Progress', line: BarsLine, solid: BarsSolid, end: false },
  { to: '/portal/resources', label: 'Resources', line: DocLine, solid: DocSolid, end: false },
  { to: '/portal/account', label: 'Account', line: UserLine, solid: UserSolid, end: false },
];

export function PortalShell() {
  const location = useLocation();
  const { logout } = usePortal();
  const signOutIdle = useCallback(() => void logout(), [logout]);
  // The Clinical Twin is a drill-down from the dashboard, drawn with a back
  // arrow and no bell or menu (mockup 6).
  const drillDown = location.pathname.startsWith('/portal/health');

  // A new screen starts at its top, as a phone app does.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-paper" style={{ color: TONE.heading }}>
      <IdleSignOut onSignOut={signOutIdle} />
      <div className="mx-auto min-h-screen w-full max-w-[430px]">
        <PortalHeader drillDown={drillDown} />
        <AwayBanner />
        {/*
          Room at the foot for the fixed tab bar and the phone's home
          indicator, on the container so no screen has to remember it.
        */}
        <main className="px-3 pb-[calc(84px+env(safe-area-inset-bottom))]">
          <Outlet />
        </main>
      </div>
      <TabBar />
    </div>
  );
}

function Wordmark({ small = false }: { small?: boolean }) {
  return (
    <Link to="/portal" className="block min-w-0" aria-label="MCOS, go to your dashboard">
      <span
        className={`block font-display font-semibold leading-none ${small ? 'text-[25px]' : 'text-[31px]'}`}
        style={{ ...DISPLAY, color: TONE.heading, letterSpacing: '-0.01em' }}
      >
        MCOS
      </span>
      <span
        className={`mt-0 block whitespace-pre font-medium uppercase leading-none ${small ? 'text-[7px]' : 'text-[8px]'}`}
        style={{ color: TONE.body, letterSpacing: '0.11em' }}
      >
        {'Metabolic health.  Real progress.'}
      </span>
    </Link>
  );
}

function Initials({ size = 36 }: { size?: number }) {
  const { me } = usePortal();
  const letters = me
    ? `${me.patient.firstName.trim().charAt(0)}${me.patient.lastName.trim().charAt(0)}`.toUpperCase()
    : '';
  return (
    <Link
      to="/portal/account"
      aria-label="Your account"
      className="grid shrink-0 place-items-center rounded-full font-display"
      style={{
        width: size,
        height: size,
        background: TONE.avatar,
        color: TONE.heading,
        fontSize: Math.round(size * 0.42),
        fontWeight: 480,
      }}
    >
      {letters}
    </Link>
  );
}

function PortalHeader({ drillDown }: { drillDown: boolean }) {
  const navigate = useNavigate();

  if (drillDown) {
    return (
      <header className="flex items-center gap-3 px-4 pb-2 pt-[max(12px,env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={() => navigate('/portal')}
          aria-label="Back to your dashboard"
          className="-ml-1 grid h-9 w-9 shrink-0 place-items-center rounded-full"
          style={{ color: TONE.forestText }}
        >
          <ArrowLeft size={22} />
        </button>
        <div className="min-w-0 flex-1">
          <Wordmark small />
        </div>
        <Initials size={33} />
      </header>
    );
  }

  return (
    <header className="flex items-center gap-3 px-4 pb-1 pt-[max(12px,env(safe-area-inset-top))]">
      <div className="min-w-0 flex-1">
        <Wordmark />
      </div>
      <div className="flex shrink-0 items-center">
        <NoticeBell />
        <span className="ml-4">
          <Initials />
        </span>
        <HeaderMenu />
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ *
 * The bell and its sheet
 * ------------------------------------------------------------------ */

function NoticeBell() {
  const { data } = usePortalQuery<{ items: PortalNotice[] }>('notifications', '/notifications');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const items = data?.items ?? [];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={items.length > 0 ? `Notifications, ${items.length} new` : 'Notifications'}
        className="relative grid h-10 w-10 place-items-center rounded-full"
        style={{ color: TONE.heading }}
      >
        <Bell size={25} strokeWidth={1.7} />
        {items.length > 0 && (
          <span
            aria-hidden="true"
            className="absolute right-[7px] top-[7px] h-[8px] w-[8px] rounded-full bg-alert ring-2 ring-paper"
          />
        )}
      </button>

      {open && (
        <Sheet title="Notifications" onClose={() => setOpen(false)}>
          {items.length === 0 ? (
            <p className="px-1 py-6 text-center text-[13px]" style={{ color: TONE.body }}>
              Nothing new right now. New reports, your plan and your monthly check-in appear here.
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: TONE.line }}>
              {items.map((item) => {
                const content = (
                  <>
                    <span
                      aria-hidden="true"
                      className="mt-[6px] h-[7px] w-[7px] shrink-0 rounded-full"
                      style={{ background: item.kind === 'checkin' ? 'var(--alert-red)' : 'var(--forest)' }}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-semibold" style={{ color: TONE.heading }}>
                        {item.title}
                      </span>
                      <span className="mt-0.5 block text-[12.5px] leading-snug" style={{ color: TONE.body }}>
                        {item.body}
                        {item.kind === 'checkin' && !item.externalHref
                          ? ' Your clinic sends you the link by text.'
                          : ''}
                      </span>
                    </span>
                  </>
                );
                return (
                  <li key={`${item.kind}-${item.at}`} style={{ borderColor: TONE.line }}>
                    {item.externalHref ? (
                      <a
                        href={item.externalHref}
                        target="_blank"
                        rel="noreferrer"
                        className="flex gap-3 py-3"
                        onClick={() => setOpen(false)}
                      >
                        {content}
                      </a>
                    ) : item.href ? (
                      <button
                        type="button"
                        className="flex w-full gap-3 py-3 text-left"
                        onClick={() => {
                          setOpen(false);
                          navigate(item.href!);
                        }}
                      >
                        {content}
                      </button>
                    ) : (
                      <div className="flex gap-3 py-3">{content}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Sheet>
      )}
    </>
  );
}

function HeaderMenu() {
  const { logout } = usePortal();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div ref={ref} className="relative ml-[14px]">
      <button
        type="button"
        aria-label="Menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="-mr-2 grid h-10 w-10 place-items-center rounded-full"
        style={{ color: TONE.heading }}
      >
        <Menu size={24} strokeWidth={1.8} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-11 z-40 w-44 overflow-hidden rounded-[10px] border bg-white py-1 shadow-[0_12px_32px_rgba(16,28,48,0.14)]"
          style={{ borderColor: TONE.line }}
        >
          {[
            // MS-001: Messages is reached from the dashboard tile and from this menu.
            { label: 'Messages', action: () => go('/portal/messages') },
            { label: 'Account', action: () => go('/portal/account') },
            { label: 'Resources', action: () => go('/portal/resources') },
          ].map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={item.action}
              className="block w-full px-4 py-2.5 text-left text-[14px] hover:bg-mint"
              style={{ color: TONE.heading }}
            >
              {item.label}
            </button>
          ))}
          <div className="my-1 border-t" style={{ borderColor: TONE.line }} />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
            className="block w-full px-4 py-2.5 text-left text-[14px] hover:bg-mint"
            style={{ color: TONE.body }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** A bottom sheet on a phone, a centred card on a wide screen. */
export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-[rgba(16,28,48,0.28)]" />
      <div className="relative w-full max-w-[430px] rounded-t-[16px] bg-white px-5 pb-[calc(20px+env(safe-area-inset-bottom))] pt-4 shadow-[0_-8px_32px_rgba(16,28,48,0.16)] sm:rounded-[16px]">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-display text-[19px] font-semibold" style={{ ...DISPLAY_MID, color: TONE.heading }}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 grid h-9 w-9 place-items-center rounded-full"
            style={{ color: TONE.body }}
          >
            <Close size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Tab bar
 * ------------------------------------------------------------------ */

function TabBar() {
  return (
    <nav
      aria-label="Sections"
      className="fixed inset-x-0 bottom-0 z-30 border-t bg-white"
      style={{ borderColor: '#e4e4e2' }}
    >
      <div className="mx-auto flex max-w-[430px] px-2 pb-[max(10px,env(safe-area-inset-bottom))]">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className="flex flex-1 flex-col items-center gap-[5px] pb-1 pt-[11px]"
          >
            {({ isActive }) => {
              const Icon = isActive ? tab.solid : tab.line;
              return (
                <>
                  <span style={{ color: isActive ? 'var(--forest)' : '#475061' }}>
                    <Icon size={24} strokeWidth={1.6} />
                  </span>
                  <span
                    className={`text-[10.5px] leading-none ${isActive ? 'font-semibold' : 'font-normal'}`}
                    style={{ color: isActive ? TONE.forestText : '#38404e' }}
                  >
                    {tab.label}
                  </span>
                </>
              );
            }}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/* ================================================================== *
 * The pieces every patient screen is built from
 * ================================================================== */

/** A white card with the mockups' faint border and 9px corners. */
export function Card({
  children,
  className = '',
  style,
  'data-testid': testId,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}) {
  return (
    <section className={`rounded-[9px] border bg-white ${className}`} style={{ borderColor: TONE.line, ...style }} data-testid={testId}>
      {children}
    </section>
  );
}

/**
 * The small uppercase label at the top of a card.
 *
 * Two weights, as drawn: the dashboard's quiet slate label, and the heavier
 * near-black one My Progress uses where the label is the card's title.
 */
export function Eyebrow({
  children,
  strong = false,
  className = '',
}: {
  children: ReactNode;
  strong?: boolean;
  className?: string;
}) {
  return (
    <p
      className={`uppercase leading-none ${strong ? 'text-[11.5px] font-semibold' : 'text-[11px] font-medium'} ${className}`}
      style={{ color: strong ? TONE.heading : '#333c4c', letterSpacing: strong ? '0.08em' : '0.05em' }}
    >
      {children}
    </p>
  );
}

/** "View Details ->": the forest link with an arrow, top right of a card. */
export function ViewLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="-my-1 inline-flex shrink-0 items-center gap-[5px] py-1 text-[11.5px] font-medium leading-none hover:underline"
      style={{ color: TONE.forestText }}
    >
      {children}
      <ArrowRight size={14} strokeWidth={1.9} />
    </Link>
  );
}

/** Header row of a card: the label on the left, the link on the right. */
export function CardHead({ label, link, strong = false }: { label: string; link?: ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Eyebrow strong={strong}>{label}</Eyebrow>
      {link}
    </div>
  );
}

/** The mint tile behind a line icon, a squircle on the plan rows. */
export function IconTile({
  children,
  size = 36,
  round = false,
  tone = 'mint',
  className = '',
}: {
  children: ReactNode;
  size?: number;
  round?: boolean;
  tone?: 'mint' | 'gold' | 'cream';
  className?: string;
}) {
  const fill = tone === 'gold' ? '#fbe7b5' : tone === 'cream' ? '#f6ead2' : '#e2ede7';
  return (
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center ${className}`}
      style={{
        width: size,
        height: size,
        background: fill,
        borderRadius: round ? '9999px' : Math.round(size * 0.39),
        color: tone === 'mint' ? '#0a3d30' : '#6b4a1e',
      }}
    >
      {children}
    </span>
  );
}

export interface RangeOption<T extends string> {
  value: T;
  label: string;
}

/**
 * The "Last 6 Months" pill. A real select underneath, so it works with a
 * keyboard, a screen reader and the phone's own picker, drawn as the pill.
 */
export function RangeSelect<T extends string>({
  value,
  options,
  onChange,
  label,
  className = '',
  compact = false,
}: {
  value: T;
  options: Array<RangeOption<T>>;
  onChange: (value: T) => void;
  label: string;
  className?: string;
  /** The smaller pill mockup 6 draws inside a card header. */
  compact?: boolean;
}) {
  const current = options.find((option) => option.value === value) ?? options[0]!;
  return (
    <label
      className={`relative inline-flex shrink-0 cursor-pointer items-center rounded-full border font-medium ${
        compact ? 'h-[24px] gap-[5px] pl-[9px] pr-[7px] text-[10.5px]' : 'h-[26px] gap-[7px] pl-[11px] pr-[9px] text-[11px]'
      } ${className}`}
      style={{ background: '#f0f2ed', borderColor: '#e2e5df', color: TONE.heading }}
    >
      <span>{current.label}</span>
      <ChevronDown size={13} strokeWidth={2} />
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A page title in the display serif, with its subtitle underneath. */
export function PageTitle({
  title,
  subtitle,
  aside,
  mark = false,
  size = 28,
  subtitleSize = 13,
  nowrap = false,
}: {
  title: string;
  subtitle?: string;
  aside?: ReactNode;
  /** A superscript trademark sign, as the Clinical Twin title carries. */
  mark?: boolean;
  /** Title size in px. The mockups draw each screen's title at its own size. */
  size?: number;
  /** Subtitle size in px, so a one line subtitle in the mockup stays one line. */
  subtitleSize?: number;
  /** Keep the title on one line, as the Clinical Twin title is drawn. */
  nowrap?: boolean;
}) {
  return (
    <div className="px-1 pb-3 pt-3">
      {/* Wraps the aside below the title on narrow phones instead of colliding. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h1
          className={`font-display font-semibold leading-[1.05] ${nowrap ? 'flex-none whitespace-nowrap' : 'min-w-0'}`}
          // The mockup size at 390 px wide, scaled down on narrower phones.
          style={{ ...DISPLAY, color: TONE.heading, letterSpacing: '-0.012em', fontSize: `clamp(${size - 6}px, ${(size / 3.9).toFixed(2)}vw, ${size}px)` }}
        >
          {title}
          {mark && <sup className="ml-[1px] align-super text-[11px] font-medium tracking-normal">™</sup>}
        </h1>
        {aside}
      </div>
      {subtitle && (
        <p className="mt-[7px] leading-snug" style={{ color: TONE.body, fontSize: subtitleSize }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export function PortalLoading() {
  return (
    <div className="grid place-items-center py-24" style={{ color: TONE.faint }}>
      <Spinner className="h-6 w-6" />
    </div>
  );
}

/**
 * The one empty state used everywhere on the patient side.
 *
 * Tone is the whole point. A patient reading their own health screen must
 * never be told that something is missing in a way that reads as a result
 * being withheld from them, so every message says what will happen and when.
 */
export function PortalEmpty({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="rounded-[9px] border border-dashed px-5 py-7 text-center" style={{ borderColor: '#d9d6cf' }}>
      <p className="font-display text-[16px] font-semibold" style={{ ...DISPLAY_MID, color: TONE.heading }}>
        {title}
      </p>
      <p className="mx-auto mt-1.5 max-w-sm text-[13px] leading-relaxed" style={{ color: TONE.body }}>
        {body}
      </p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function PortalError({ message, retry }: { message: string; retry?: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="mt-4 rounded-[9px] border border-alert-line bg-alert-soft px-5 py-6 text-center">
      <p className="text-[14px]" style={{ color: TONE.heading }}>
        {message}
      </p>
      <div className="mt-3 flex justify-center gap-2">
        {retry && (
          <button
            type="button"
            onClick={retry}
            className="rounded-lg border bg-white px-3 py-1.5 text-[12px] font-semibold"
            style={{ borderColor: TONE.line, color: TONE.heading }}
          >
            Try again
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate('/portal')}
          className="rounded-lg px-3 py-1.5 text-[12px] font-semibold"
          style={{ color: TONE.body }}
        >
          Back to dashboard
        </button>
      </div>
    </div>
  );
}

/* ================================================================== *
 * Formatting
 * ================================================================== */

export function formatPortalDate(value: string | null | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** "Dec 2024", as the progress card prints a start date. */
export function formatMonthYear(value: string): string {
  return new Date(value).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** A number with at most `decimals` places, trailing zeros kept to that precision. */
export function formatNumber(value: number, decimals = 0): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** A weight: whole pounds print whole, anything else to one place. */
export function formatLbs(value: number): string {
  return Number.isInteger(value) ? formatNumber(value, 0) : formatNumber(value, 1);
}

/** The clinic's provider away notice, between its dates only. */
function AwayBanner() {
  const { data } = usePortalQuery<{ away: { until: string; note: string | null } | null }>('away', '/away');
  if (!data?.away) return null;
  const back = new Date(`${data.away.until}T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
  return (
    <div className="mx-[14px] mb-[6px] rounded-[12px] px-[12px] py-[9px] text-[13px] leading-snug" style={{ background: '#fff4e5', color: '#7a4b12' }} role="status" data-testid="away-banner">
      Your clinician is away until {back}. Replies may be slower.
      {data.away.note ? ` ${data.away.note}` : ''}
    </div>
  );
}
